import { isDesktop } from '@/consts/device';
import getFile from '@/requests/cloud-storage/getFile';
import getMediaListFromFlowId from '@/requests/magic-flow/getMediaListFromFlowId';
import { getAppStore, setAppStore } from '@/store';
import { LightboxSource } from '@/store/lightbox';
import { RightPanelTab } from '@/store/navigation/types';
import createContentFile from '@/utils/media/createContentFile';
import { driveFileByIdToContentFile } from '@/utils/media/driveFileToContentFile';
import { clearGeneration } from '@/utils/storage/generationStorage';
import { Screen } from '@/types';

import type { ContentFile, FlowMedia } from '@/types';
import type { Deeplink } from '../types';
import { DeeplinkKind } from '../types';

type TaskDeeplink = Extract<Deeplink, { kind: DeeplinkKind.Task }>;

/**
 * One result as the grid would show it. The media (url, preview, size) comes
 * from the flow result — the by-id Drive response only has `contentLink`,
 * which does not load as an image. The Drive file adds what the flow result
 * lacks: name, attributes (reaction, preset slug for Reuse template) and the
 * run id.
 */
const toContentFile = async (media: FlowMedia): Promise<ContentFile> => {
  const { contentFile } = createContentFile({ media });
  const driveFile = media.driveUid
    ? await getFile({ uid: media.driveUid })
    : null;
  if (!driveFile) return contentFile;

  const { name, attributes, runId } = driveFileByIdToContentFile(driveFile);
  return { ...contentFile, name, attributes, runId };
};

/**
 * Notification deeplink: fetch the task's results and open them in the app
 * lightbox. They are its own items — nothing is added to the creations list,
 * so a generation of any age opens as is.
 */
const taskHandler = async ({ taskId }: TaskDeeplink): Promise<void> => {
  setAppStore({ screen: Screen.Loading, hasAutoSelectedPreset: true });

  try {
    const mediaList = await getMediaListFromFlowId(taskId);
    const items = await Promise.all(mediaList.map(toContentFile));

    setAppStore({
      resultFile: mediaList[0],
      screen: Screen.Files,
      ...(isDesktop && { rightPanelTab: RightPanelTab.Creations }),
      hasAutoSelectedPreset: false,
    });
    getAppStore().openLightbox({
      items,
      source: LightboxSource.Deeplink,
      resultMode: true,
    });

    // The user has opened this result via the notification, so drop its
    // persisted crumb.
    await clearGeneration(taskId);
  } catch {
    setAppStore({ screen: Screen.Library, hasAutoSelectedPreset: false });
  }
};

export default taskHandler;
